import React, { useState } from "react"
import { PortfolioData as data } from "../../data"

export const Gallery = () => {
    const [galleryItemPreview, setGalleryItemPreview] = useState<undefined | number>(undefined)
    const [currentGalleryElement, setCurrentGalleryElement] = useState<number>(0)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const imageRef = React.useRef<HTMLImageElement | null>(null)

    // Touch state for swipe
    const touchStartX = React.useRef<number | null>(null)
    const touchEndX = React.useRef<number | null>(null)
    const touchStartY = React.useRef<number | null>(null)
    const touchEndY = React.useRef<number | null>(null)

    // Prevent body scroll when lightbox is open
    React.useEffect(() => {
        if (galleryItemPreview !== undefined) {
            const originalOverflow = document.body.style.overflow
            document.body.style.overflow = "hidden"
            return () => {
                document.body.style.overflow = originalOverflow
            }
        }
    }, [galleryItemPreview])

    // Handle Fullscreen API
    React.useEffect(() => {
        function handleFullscreenChange() {
            const fsElement = document.fullscreenElement || (document as any).webkitFullscreenElement
            setIsFullscreen(!!fsElement)
        }
        document.addEventListener("fullscreenchange", handleFullscreenChange)
        document.addEventListener("webkitfullscreenchange", handleFullscreenChange)
        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange)
            document.removeEventListener("webkitfullscreenchange", handleFullscreenChange)
        }
    }, [])

    const enterFullscreen = () => {
        if (imageRef.current) {
            if (imageRef.current.requestFullscreen) {
                imageRef.current.requestFullscreen()
            } else if ((imageRef.current as any).webkitRequestFullscreen) {
                (imageRef.current as any).webkitRequestFullscreen()
            }
        }
    }

    const exitFullscreen = () => {
        if (document.exitFullscreen) {
            document.exitFullscreen()
        } else if ((document as any).webkitExitFullscreen) {
            (document as any).webkitExitFullscreen()
        }
    }

    const handleTouchStart = (e: React.TouchEvent<HTMLImageElement>) => {
        touchStartX.current = e.touches[0].clientX
        touchStartY.current = e.touches[0].clientY
    }

    const handleTouchMove = (e: React.TouchEvent<HTMLImageElement>) => {
        touchEndX.current = e.touches[0].clientX
        touchEndY.current = e.touches[0].clientY
    }

    const handleTouchEnd = (elementsLength: number) => {
        // Swipe left/right for gallery navigation
        if (
            touchStartX.current !== null &&
            touchEndX.current !== null &&
            touchStartY.current !== null &&
            touchEndY.current !== null
        ) {
            const diffX = touchStartX.current - touchEndX.current
            const diffY = touchStartY.current - touchEndY.current

            // Swipe left/right
            if (Math.abs(diffX) > 50 && Math.abs(diffX) > Math.abs(diffY)) {
                if (diffX > 0 && currentGalleryElement < elementsLength - 1) {
                    // swipe left
                    setCurrentGalleryElement((prev) => prev + 1)
                } else if (diffX < 0 && currentGalleryElement > 0) {
                    // swipe right
                    setCurrentGalleryElement((prev) => prev - 1)
                }
            }

            // Swipe down to exit fullscreen
            if (isFullscreen && diffY < -70 && Math.abs(diffY) > Math.abs(diffX)) {
                exitFullscreen()
            }
        }
        touchStartX.current = null
        touchEndX.current = null
        touchStartY.current = null
        touchEndY.current = null
    }

    const renderGalleryLightBox = () => {
        if (galleryItemPreview === undefined) return null

        const elements = data[galleryItemPreview].elements

        const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
            if (e.target === e.currentTarget) {
                setGalleryItemPreview(undefined)
                setCurrentGalleryElement(0)
            }
        }

        return (
            <div
                className="lightbox"
                style={{
                    position: "fixed",
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: "rgba(0,0,0,0.9)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000
                }}
                onClick={handleBackdropClick}
            >
                <div
                    className="lightbox-content"
                    style={{
                        background: "none",
                        padding: 0,
                        borderRadius: 0,
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center"
                    }}
                >
                    {/* Left Arrow */}
                    <button
                        onClick={() =>
                            setCurrentGalleryElement((prev) =>
                                prev > 0 ? prev - 1 : prev
                            )
                        }
                        disabled={currentGalleryElement === 0}
                        style={{
                            background: "none",
                            border: "none",
                            color: "#fff",
                            fontSize: "2.5rem",
                            cursor: currentGalleryElement === 0 ? "not-allowed" : "pointer",
                            marginRight: "20px",
                            opacity: currentGalleryElement === 0 ? 0.4 : 1,
                            userSelect: "none"
                        }}
                        aria-label="Назад"
                    >
                        <img
                            src="assets/images/prev.png"
                        />
                    </button>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
                        <img
                            ref={imageRef}
                            src={elements[currentGalleryElement]}
                            alt={`Фото ${currentGalleryElement + 1}`}
                            style={{
                                width: "80vw",
                                height: "80vh",
                                maxWidth: "80vw",
                                maxHeight: "80vh",
                                objectFit: "contain",
                                marginBottom: 0,
                                borderBottom: "8px solid #222",
                                // Для мобильных устройств
                                touchAction: "none"
                            }}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={() => handleTouchEnd(elements.length)}
                        />
                        {/* Fullscreen and Close buttons */}
                        <div style={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 8, zIndex: 2 }}>
                            {!isFullscreen && (
                                <button
                                    onClick={enterFullscreen}
                                    style={{
                                        background: "rgba(0,0,0,0.6)",
                                        border: "none",
                                        color: "#fff",
                                        padding: "8px 12px",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        fontSize: "1.2rem",
                                        zIndex: 2
                                    }}
                                    aria-label="Во весь экран"
                                >
                                    ⛶
                                </button>
                            )}
                            {/* Close button (always visible in lightbox) */}
                            <button
                                onClick={() => {
                                    setGalleryItemPreview(undefined)
                                    setCurrentGalleryElement(0)
                                }}
                                style={{
                                    background: "rgba(0,0,0,0.7)",
                                    border: "none",
                                    color: "#fff",
                                    padding: "8px 12px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "1.2rem",
                                    marginLeft: "4px"
                                }}
                                aria-label="Закрыть"
                            >
                                ✕
                            </button>
                        </div>
                        {/* Fullscreen exit button (fixed, only in fullscreen) */}
                        {isFullscreen && (
                            <button
                                onClick={exitFullscreen}
                                style={{
                                    position: "fixed",
                                    top: 10,
                                    right: 10,
                                    background: "rgba(0,0,0,0.8)",
                                    border: "none",
                                    color: "#fff",
                                    padding: "14px 18px",
                                    borderRadius: "50%",
                                    cursor: "pointer",
                                    fontSize: "2rem",
                                    zIndex: 2000,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
                                }}
                                aria-label="Выйти из полноэкранного режима"
                            >
                                ✕
                            </button>
                        )}
                        <div
                            style={{
                                background: "#222",
                                padding: "0px 0 10px 0",
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center"
                            }}
                        >
                            <div style={{ marginTop: 10, color: "#fff" }}>
                                {currentGalleryElement + 1} / {elements.length}
                            </div>
                        </div>
                    </div>
                    {/* Right Arrow */}
                    <button
                        onClick={() =>
                            setCurrentGalleryElement((prev) =>
                                prev < elements.length - 1 ? prev + 1 : prev
                            )
                        }
                        disabled={currentGalleryElement === elements.length - 1}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: currentGalleryElement === elements.length - 1 ? "not-allowed" : "pointer",
                            marginLeft: "20px",
                            opacity: currentGalleryElement === elements.length - 1 ? 0.4 : 1,
                            userSelect: "none"
                        }}
                        aria-label="Вперёд"
                    >
                        <img
                            src="assets/images/next.png"
                        />
                    </button>
                </div>
            </div>
        )
    }

    const handleClickGalleryItem = (itemIdx: number) => {
        setGalleryItemPreview(itemIdx)
    }

    return (
        <section className="portfolio" id="portfolio">
            <div className="container">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="section-heading">
                            <h2>Наши работы</h2>
                        </div>
                        <div
                            className="portfolio-gallery"
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                justifyContent: "center"
                            }}>
                            {data.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="portfolio-cover"
                                    onClick={() => handleClickGalleryItem(idx)}
                                    style={{
                                        cursor: "pointer",
                                        display: "inline-block",
                                        margin: "10px",
                                        width: "260px",
                                        height: "160px",
                                        overflow: "hidden",
                                        borderRadius: "3px",
                                        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                                        background: "#222",
                                    }}
                                >
                                    <img className="thumbnail" src={item.cover} alt={`Обложка ${idx + 1}`} />
                                </div>
                            ))}
                        </div>
                        {renderGalleryLightBox()}
                    </div>
                </div>
            </div>
        </section>
    )
}
